import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { CustomerListTable } from "@/components/customers/CustomerListTable";
import CustomerListV2 from "@/components/customers/list/CustomerListV2";
import { NewCustomerDialog } from "@/components/new-project/NewCustomerDialog";
import { useCanCreateCustomer } from "@/hooks/use-can-create-customer";
import { useCustomersWithProjectCounts } from "@/hooks/use-customers";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

/**
 * /kunder. Med feature_crm_kundekort vises kundeliste v2 (VIB-2891: tellere,
 * hast-gruppering, GEEK-kolonner). Uten flagget: dagens liste fra PowerOffice
 * med søk og prosjektantall. «Ny kunde» vises for dem databasen lar opprette
 * kunder, og sender til kundekortet etterpå.
 */
export default function CustomersPage() {
  const crm = useFeatureFlag("feature_crm_kundekort");
  const canCreate = useCanCreateCustomer();
  const navigate = useNavigate();
  const openCustomer = (id: string) => navigate(`/kunde/${id}`);
  return (
    <div className="page-container animate-fade-in">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
          <SrIcon name="building-2" className="h-6 w-6" />
          Kunder
        </h1>
        {canCreate && (
          <NewCustomerDialog
            source="kunder"
            onCustomerCreated={openCustomer}
            onOpenExisting={openCustomer}
            trigger={
              <Button type="button" aria-label="Ny kunde">
                <SrIcon name="plus" className="h-4 w-4" />
                Ny<span className="hidden sm:inline"> kunde</span>
              </Button>
            }
          />
        )}
      </div>
      {crm ? <CustomerListV2 /> : <LegacyCustomerList />}
    </div>
  );
}

function LegacyCustomerList() {
  const { data: customers = [], isLoading, error } = useCustomersWithProjectCounts();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      [c.name, c.organization_number, c.contact_person, c.country_code].some((field) => field?.toLowerCase().includes(q)),
    );
  }, [customers, search]);

  if (error) {
    return <p className="text-destructive">Feil ved lasting av kunder: {error.message}</p>;
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[16rem]">
            <SrIcon name="search" className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søk på navn, org.nr eller kontakt …"
              className="pl-8"
              aria-label="Søk i kunder"
            />
          </div>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <SrIcon name="loader2" className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <CustomerListTable customers={filtered} emptyMessage={search ? "Ingen kunder matcher søket." : "Ingen kunder."} />
        )}
      </CardContent>
    </Card>
  );
}
